import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '../supabase';

const ONBOARDING_COLUMNS = ['novo_cliente', 'contrato', 'acessos', 'planejamento', 'criativos', 'implementado'];
const ONBOARDING_LABELS: any = { novo_cliente: 'Novo Cliente', contrato: 'Contrato', acessos: 'Acessos', planejamento: 'Planejamento', criativos: 'Criativos', implementado: 'Implementado' };
const ONBOARDING_ICONS: any = { novo_cliente: '🆕', contrato: '📝', acessos: '🔑', planejamento: '📊', criativos: '🎨', implementado: '✅' };

const DEMANDAS_COLUMNS = ['backlog', 'todo', 'wip', 'review', 'wait', 'block', 'done'];
const DEMANDAS_LABELS: any = { backlog: 'Backlog', todo: 'To Do', wip: 'WIP', review: 'Review', wait: 'Wait', block: 'Block', done: 'Done' };
const DEMANDAS_ICONS: any = { backlog: '📋', todo: '📌', wip: '⚡', review: '👀', wait: '⏳', block: '🚫', done: '✅' };

const CRM_COLUMNS = ['backlog', 'todo', 'wip', 'wait', 'done'];
const CRM_LABELS: any = { backlog: 'Backlog', todo: 'To Do', wip: 'WIP', wait: 'Wait', done: 'Done' };
const CRM_ICONS: any = { backlog: '📋', todo: '📌', wip: '⚡', wait: '⏳', done: '✅' };

const CRM_CARDS_TEMPLATE = [
  'SOLICITAR E-MAIL E SENHA AO CLIENTE',
  'CRIAR CONTA NO KOMMO VIA PORTAL DE PARCEIROS',
  'CONFIGURAR PIPELINE DE VENDAS',
  'CONFIGURAR CAMPOS PERSONALIZADOS',
  'CRIAR TAGS DE ORGANIZAÇÃO',
  'CONECTAR USUÁRIOS E VENDEDORES',
  'CONFIGURAR ROBÔS DE AUTOMAÇÃO (SALESBOTS)',
  'CONFIGURAR PAINEL DE MONITOR COMERCIAL',
  'CONFIGURAR WHATSAPP API',
  'MAPEAR E LOGAR FONTES DE LEADS',
  'ENTREGA FINAL',
];

const ONBOARDING_CHECKLIST_TEMPLATE = [
  { section: 'ACESSOS', items: ['Criar grupo WhatsApp', 'Mensagem de apresentação', 'Solicitar acessos necessários', 'Receber acesso de parceiro da BM'] },
  { section: 'PLANEJAMENTO', items: ['Análise de estrutura'] },
  { section: 'CRIATIVOS', items: ['Solicitar criativos', 'Receber criativos', 'Editar criativos', 'Aprovação do cliente'] },
  { section: 'ESTRUTURA', items: ['Subir estrutura de campanhas'] },
  { section: 'FINALIZAÇÃO', items: ['Card de acompanhamento criado', 'Comunicar cliente'] },
];

const DEMANDAS_CHECKLIST_TEMPLATES: any = {
  'CRIATIVOS NOVOS': [
    'Criativos recebidos/produzidos',
    'Enviados para aprovação',
    'Aprovado pelo cliente',
    'Criativos subidos',
    'Revisão feita (nomenclatura, orçamento, público, copy, criativo, mensagens prontas)',
    'Card de acompanhamento criado',
    'Cliente avisado',
  ],
  'RESET DE CAMPANHAS': [
    'Campanhas antigas pausadas corretamente',
    'Estrutura duplicada ou recriada corretamente',
    'Configurações conferidas (objetivo, público, orçamento, destino)',
    'Criativos corretos aplicados',
    'Mensagem pronta revisada',
    'Revisão geral feita',
    'Card de acompanhamento criado',
    'Cliente avisado',
  ],
  'ESTRUTURA NOVO CLIENTE': [
    'Nomenclatura correta (campanha, conjunto, anúncio)',
    'Objetivo + otimização corretos',
    'Público + localização corretos',
    'Dispositivo correto (se upgrade/iOS)',
    'Orçamento + programação corretos',
    'Criativo + copy + mensagem correta',
    'Revisão geral feita',
    'Campanha publicada e ativa',
    'Card de acompanhamento criado',
    'Cliente avisado',
  ],
  'PIX SEMANAL': [
    'Verificado saldo da conta',
    'Pix enviado para o cliente',
    'Saldo adicionado corretamente',
    'Campanhas ativas após o pix feito',
  ],
  'ACOMPANHAMENTO 3 DIAS': [
    'Resultados',
    'Volume de mensagens',
    'Custos por resultados',
    'Verificado se está dentro do padrão e otimizado',
    'Feedback enviado para o cliente',
    'Card de acompanhamento 7 dias criado',
  ],
  'ACOMPANHAMENTO 7 DIAS': [
    'Resultados',
    'Volume de mensagens',
    'Custos por resultados',
    'Verificado se está dentro do padrão e otimizado',
    'Feedback enviado para o cliente',
  ],
  'ALTERAÇÃO DE ORÇAMENTO': [
    'Entendi o novo orçamento (diário ou mensal)',
    'Alterei na planilha de relatório diário e planejamento (se necessário)',
    'Alterei no gerenciador de anúncios',
    'Revisei as planilhas e gerenciador de anúncios',
    'Alteração publicada',
    'Cliente avisado',
  ],
  'DEMANDAS KOMMO': [
    'Entendi a demanda (estrutura / usuários / porcentagem / robô)',
    'Configuração realizada no sistema',
    'Teste feito (funcionando corretamente)',
    'Revisei as planilhas e gerenciador de anúncios',
    'Revisão final feita',
    'Cliente avisado',
  ],
  'PAUSA DE CRIATIVOS': [
    'Localizei o criativo correto (campanha/conjunto/anúncio)',
    'Criativo pausado corretamente (se necessário redirecionar o orçamento)',
    'Revisão feita',
    'Cliente avisado',
  ],
  'SOLICITAR CRIATIVOS': [
    'Solicitei novos criativos',
    'Enviei referências e ideias de criativos',
    'Criar card de acompanhamento para aguardar os criativos',
  ],
};

const PROJECT_COLORS = ['#8b5cf6', '#F5A623', '#10b981', '#ef4444', '#6366f1', '#ec4899', '#f97316', '#06b6d4'];
const TEAM_COLORS: any = { MANHATTAN: '#F5A800', BROOKLYN: '#6366f1' };

const TYPE_COLORS: any = {
  task: ['#f3f4f6', '#374151'], config: ['#eff6ff', '#1d4ed8'], video: ['#fef2f2', '#dc2626'],
  entrega: ['#f0fdf4', '#16a34a'], reuniao: ['#fefce8', '#ca8a04'], robo: ['#f5f3ff', '#7c3aed'],
};
const TAG_COLORS = ['#111', '#F5A623', '#ef4444', '#10b981', '#6366f1', '#ec4899', '#8b5cf6', '#f97316', '#06b6d4', '#84cc16'];
const TEAM_MEMBERS = ['Kennedy', 'Siara', 'Eber', 'Eliabe', 'Isa', 'Junior'];
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const formatCardTitle = (projectName: string, demanda: string) => `[${projectName.toUpperCase()}] - [${demanda.toUpperCase()}]`;
const extractDemanda = (title: string, projectName: string) => {
  const prefix = `[${projectName.toUpperCase()}] - [`;
  if (title.startsWith(prefix) && title.endsWith(']')) return title.slice(prefix.length, -1);
  return title;
};

const isOverdue = (due_date: string) => {
  if (!due_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(due_date + 'T00:00:00');
  return due < today;
};

const isToday = (due_date: string) => {
  if (!due_date) return false;
  const today = new Date();
  const due = new Date(due_date + 'T00:00:00');
  return due.getFullYear() === today.getFullYear() &&
    due.getMonth() === today.getMonth() &&
    due.getDate() === today.getDate();
};

const computeNextDate = (type: string, days: string[], dayOfMonth: number | null, time: string | null): string | null => {
  if (type === 'none') return null;
  const now = new Date();
  const [h, m] = (time || '09:00').split(':').map(Number);
  let next = new Date(now);
  next.setSeconds(0, 0);
  if (type === 'daily') { next.setDate(now.getDate() + 1); next.setHours(h, m); }
  else if (type === 'weekly') {
    const dayNums = days.map(d => WEEKDAYS.indexOf(d)).filter(n => n >= 0);
    if (dayNums.length === 0) return null;
    for (let i = 1; i <= 7; i++) {
      const candidate = new Date(now);
      candidate.setDate(now.getDate() + i);
      if (dayNums.includes(candidate.getDay())) { candidate.setHours(h, m, 0, 0); return candidate.toISOString(); }
    }
    return null;
  } else if (type === 'monthly') {
    next = new Date(now.getFullYear(), now.getMonth() + 1, dayOfMonth || 1, h, m, 0, 0);
  }
  return next.toISOString();
};

const renderDescription = (description: string) => {
  if (!description) return null;
  const lines = description.split('\n').map(l => l.trim()).filter(Boolean);
  const checklistStart = lines.findIndex(l => l.includes('✅') || l.toLowerCase().includes('definição de pronto'));
  const steps = checklistStart > 0 ? lines.slice(0, checklistStart) : lines;
  const cleanSteps = steps.map(l => l.replace(/^\d+[.)]\s*/, '').trim()).filter(Boolean);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, margin: '6px 0' }}>
        {cleanSteps.map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 11, color: '#3a3a35', lineHeight: 1.4, padding: '5px 8px', background: '#f5f5f0', borderRadius: 6 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: '#8a8a80', minWidth: 16, marginTop: 1, flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</div>
            <div style={{ flex: 1 }}>{step}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const groupChecklistBySections = (checklist: any[]) => {
  const sections: { name: string; items: { item: any; idx: number }[] }[] = [];
  const noSection: { item: any; idx: number }[] = [];
  checklist.forEach((item, idx) => {
    const match = item.text.match(/^\[([^\]]+)\]\s(.+)$/);
    if (match) {
      const sectionName = match[1];
      const existing = sections.find(s => s.name === sectionName);
      if (existing) existing.items.push({ item: { ...item, text: match[2] }, idx });
      else sections.push({ name: sectionName, items: [{ item: { ...item, text: match[2] }, idx }] });
    } else {
      noSection.push({ item, idx });
    }
  });
  return { sections, noSection };
};

export default function Dashboard({ session, board, workspace, onBack }: { session: any; board: any; workspace: any; onBack: () => void }) {
  const [projects, setProjects] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [cards, setCards] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [activeSprint, setActiveSprint] = useState<number | null>(null);
  const [activeProject, setActiveProject] = useState<string>('all');
  const [activeTeamFilter, setActiveTeamFilter] = useState<string>('all');
  const [activeMemberFilter, setActiveMemberFilter] = useState<string>('all');
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [showNewCard, setShowNewCard] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [editingCard, setEditingCard] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [newCheckItem, setNewCheckItem] = useState('');
  const [newCheckSection, setNewCheckSection] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', client: '', deadline: '', v1_deadline: '' });
  const [newCard, setNewCard] = useState({ title: '', description: '', card_type: 'task', sprint: 1, due_date: '', project_id: '', tags: [] as string[], client_id: '', checklist_template: '' });
  const [newTag, setNewTag] = useState({ name: '', color: '#111' });
  const [loading, setLoading] = useState(true);
  const [creatingProject, setCreatingProject] = useState(false);
  const autoSaveTimer = useRef<any>(null);

  const isOnboarding = workspace?.type === 'onboarding';
  const isCRM = workspace?.type === 'crm';
  const isDemandas = workspace?.type === 'demandas';

  const COLUMNS = isOnboarding ? ONBOARDING_COLUMNS : isCRM ? CRM_COLUMNS : DEMANDAS_COLUMNS;
  const COLUMN_LABELS = isOnboarding ? ONBOARDING_LABELS : isCRM ? CRM_LABELS : DEMANDAS_LABELS;
  const COLUMN_ICONS = isOnboarding ? ONBOARDING_ICONS : isCRM ? CRM_ICONS : DEMANDAS_ICONS;
  const wsColor = isCRM ? '#8b5cf6' : isOnboarding ? '#F5A800' : '#10b981';

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fetchAll = async () => {
    setLoading(true);
    const { data: proj } = await supabase.from('projects').select('*').order('created_at');
    const { data: cds } = await supabase.from('cards').select('*').order('sort_order').order('created_at');
    const { data: tgs } = await supabase.from('tags').select('*').order('created_at');
    const { data: cls } = await supabase.from('clients').select('*').order('name');
    setProjects(proj || []);
    setCards((cds || []).filter((c: any) => !c.archived && c.board_id === board?.id));
    setTags(tgs || []);
    setClients(cls || []);
    setLoading(false);
  };

  const autoSave = useCallback(async (form: any, cardId: string, projs: any[]) => {
    if (!cardId) return;
    const projectName = projs.find(p => p.id === form.project_id)?.name || '';
    const formattedTitle = isOnboarding ? form.demanda : (isCRM ? formatCardTitle(projectName, form.demanda || '') : form.demanda || '');
    const recurrence_next_at = computeNextDate(form.recurrence_type, form.recurrence_days || [], form.recurrence_day_of_month, form.recurrence_time);
    setSaving(true);
    await supabase.from('cards').update({
      title: formattedTitle, description: form.description, card_type: form.card_type,
      sprint: form.sprint, due_date: form.due_date || null, project_id: form.project_id || null,
      tags: form.tags || [], checklist: form.checklist || [], members: form.members || [],
      urgency: form.urgency === true, recurrence_type: form.recurrence_type || 'none',
      recurrence_days: form.recurrence_days || [], recurrence_day_of_month: form.recurrence_day_of_month || null,
      recurrence_time: form.recurrence_time || null, recurrence_next_at: recurrence_next_at || null,
      client_id: form.client_id || null, priority: form.priority || 0,
    }).eq('id', cardId);
    setSaving(false); setSavedOk(true);
    setTimeout(() => setSavedOk(false), 2000);
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, title: formattedTitle, ...form } : c));
  }, [isOnboarding, isCRM]);

  const scheduleAutoSave = useCallback((form: any, cardId: string, projs: any[], delay = 800) => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => autoSave(form, cardId, projs), delay);
  }, [autoSave]);

  const saveNow = useCallback(async (form: any, cardId: string, projs: any[]) => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    await autoSave(form, cardId, projs);
  }, [autoSave]);

  const updateField = (field: string, value: any, immediate = false) => {
    const newForm = { ...editForm, [field]: value };
    setEditForm(newForm);
    if (editingCard) {
      if (immediate) saveNow(newForm, editingCard.id, projects);
      else scheduleAutoSave(newForm, editingCard.id, projects);
    }
  };

  const createProject = async () => {
    if (!newProject.name) return;
    setCreatingProject(true);
    const { data: proj } = await supabase.from('projects').insert([{ ...newProject, user_id: session.user.id }]).select().single();
    if (proj && isCRM) {
      const crmCards = CRM_CARDS_TEMPLATE.map((title, i) => ({
        title: formatCardTitle(proj.name, title), column_name: 'backlog',
        board_id: board?.id, workspace_id: workspace?.id, project_id: proj.id,
        card_type: 'task', sprint: 1, sort_order: i, checklist: [], tags: [], members: [],
        urgency: false, recurrence_type: 'none', recurrence_days: [], priority: 0,
      }));
      await supabase.from('cards').insert(crmCards);
    }
    setNewProject({ name: '', client: '', deadline: '', v1_deadline: '' });
    setShowNewProject(false); setCreatingProject(false); fetchAll();
  };

  const deleteProject = async (projectId: string) => {
    if (!window.confirm('Excluir este projeto e todos os seus cards?')) return;
    await supabase.from('cards').delete().eq('project_id', projectId);
    await supabase.from('projects').delete().eq('id', projectId);
    if (activeProject === projectId) setActiveProject('all');
    fetchAll();
  };

  const createCard = async (column: string) => {
    if (!newCard.title) return;
    const projectName = projects.find(p => p.id === newCard.project_id)?.name || '';
    const formattedTitle = isOnboarding ? newCard.title : isCRM ? formatCardTitle(projectName, newCard.title) : newCard.title;
    const checklist = isOnboarding
      ? ONBOARDING_CHECKLIST_TEMPLATE.flatMap(s => s.items.map(item => ({ text: `[${s.section}] ${item}`, done: false })))
      : isDemandas && newCard.checklist_template
      ? (DEMANDAS_CHECKLIST_TEMPLATES[newCard.checklist_template] || []).map((item: string) => ({ text: item, done: false }))
      : [];
    await supabase.from('cards').insert([{
      ...newCard, title: formattedTitle, column_name: column,
      board_id: board?.id, workspace_id: workspace?.id,
      project_id: newCard.project_id || null, client_id: newCard.client_id || null,
      checklist, priority: 0,
    }]);
    setNewCard({ title: '', description: '', card_type: 'task', sprint: 1, due_date: '', project_id: '', tags: [], client_id: '', checklist_template: '' });
    setShowNewCard(null); fetchAll();
  };

  const createTag = async () => {
    if (!newTag.name) return;
    await supabase.from('tags').insert([{ ...newTag, user_id: session.user.id }]);
    setNewTag({ name: '', color: '#111' }); fetchAll();
  };

  const deleteTag = async (tagId: string) => { await supabase.from('tags').delete().eq('id', tagId); fetchAll(); };

  const deleteCard = async (cardId: string) => {
    await supabase.from('cards').delete().eq('id', cardId);
    setEditingCard(null); fetchAll();
  };

  const openEditCard = (card: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const projectName = projects.find(p => p.id === card.project_id)?.name || '';
    const demanda = isOnboarding ? card.title : (isCRM ? extractDemanda(card.title, projectName) : card.title);
    setEditForm({
      demanda, description: card.description || '', card_type: card.card_type || 'task',
      sprint: card.sprint || 1, due_date: card.due_date || '', project_id: card.project_id || '',
      tags: card.tags || [], checklist: card.checklist || [], members: card.members || [],
      urgency: card.urgency === true, recurrence_type: card.recurrence_type || 'none',
      recurrence_days: card.recurrence_days || [], recurrence_day_of_month: card.recurrence_day_of_month || null,
      recurrence_time: card.recurrence_time || '09:00', client_id: card.client_id || '',
      priority: card.priority || 0,
    });
    setEditingCard(card);
  };

  const toggleCheckItem = (idx: number) => {
    const updated = editForm.checklist.map((item: any, i: number) => i === idx ? { ...item, done: !item.done } : item);
    const newForm = { ...editForm, checklist: updated };
    setEditForm(newForm);
    if (editingCard) saveNow(newForm, editingCard.id, projects);
  };

  const addCheckItem = () => {
    if (!newCheckItem.trim()) return;
    const text = newCheckSection ? `[${newCheckSection}] ${newCheckItem.trim()}` : newCheckItem.trim();
    const updated = [...editForm.checklist, { text, done: false }];
    const newForm = { ...editForm, checklist: updated };
    setEditForm(newForm); setNewCheckItem('');
    if (editingCard) saveNow(newForm, editingCard.id, projects);
  };

  const removeCheckItem = (idx: number) => {
    const updated = editForm.checklist.filter((_: any, i: number) => i !== idx);
    const newForm = { ...editForm, checklist: updated };
    setEditForm(newForm);
    if (editingCard) saveNow(newForm, editingCard.id, projects);
  };

  const onDragEnd = async (result: any) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const card = cards.find(c => c.id === draggableId);
    const newCol = destination.droppableId;
    if (newCol === 'done' && card?.recurrence_type && card.recurrence_type !== 'none') {
      await supabase.from('cards').update({ archived: true }).eq('id', draggableId);
      const recurrence_next_at = computeNextDate(card.recurrence_type, card.recurrence_days || [], card.recurrence_day_of_month, card.recurrence_time);
      await supabase.from('cards').insert([{ ...card, id: undefined, column_name: 'backlog', checklist: (card.checklist || []).map((i: any) => ({ ...i, done: false })), recurrence_next_at }]);
      fetchAll(); return;
    }
    setCards(prev => prev.map(c => c.id === draggableId ? { ...c, column_name: newCol } : c));
    await supabase.from('cards').update({ column_name: newCol }).eq('id', draggableId);
  };

  const filteredCards = (column: string) => {
    const filtered = cards.filter(c => {
      const matchProject = activeProject === 'all' || c.project_id === activeProject;
      const matchSprint = activeSprint === null || c.sprint === activeSprint;
      const client = clients.find(cl => cl.id === c.client_id);
      const matchTeam = activeTeamFilter === 'all' || client?.team === activeTeamFilter;
      const matchMember = activeMemberFilter === 'all' || (c.members || []).includes(activeMemberFilter);
      return c.column_name === column && matchProject && matchSprint && matchTeam && matchMember;
    });
    return filtered.sort((a, b) => {
      if (a.priority && b.priority) return a.priority - b.priority;
      if (a.priority && !b.priority) return -1;
      if (!a.priority && b.priority) return 1;
      return 0;
    });
  };

  const getProjectColor = (index: number) => PROJECT_COLORS[index % PROJECT_COLORS.length];
  const availableSprints = Array.from(new Set(cards.map(c => c.sprint).filter(Boolean))).sort((a: any, b: any) => a - b);
  const totalCards = cards.length;
  const doneCards = cards.filter(c => c.column_name === 'done').length;
  const overdueCards = cards.filter(c => isOverdue(c.due_date) && c.column_name !== 'done').length;

  const glass: any = { background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' };
  const glassModal: any = { background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' };
  const input: any = { width: '100%', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', color: '#111', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 14, fontFamily: 'inherit' };
  const lbl: any = { fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 6 };
  const btn = (primary: boolean): any => ({ background: primary ? '#111' : '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', color: primary ? '#fff' : '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });

  const totalChecked = editForm.checklist?.filter((i: any) => i.done).length || 0;
  const totalCheckItems = editForm.checklist?.length || 0;
  const overallProgress = totalCheckItems > 0 ? Math.round(totalChecked / totalCheckItems * 100) : 0;

  const renderChecklist = () => {
    if (!editForm.checklist || editForm.checklist.length === 0) return null;
    const { sections, noSection } = groupChecklistBySections(editForm.checklist);
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, minWidth: 32 }}>{overallProgress}%</span>
          <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: overallProgress === 100 ? '#10b981' : wsColor, width: `${overallProgress}%`, borderRadius: 3, transition: 'width .3s' }} />
          </div>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{totalChecked}/{totalCheckItems}</span>
        </div>
        {sections.map((section, si) => {
          const sectionDone = section.items.filter(i => i.item.done).length;
          const sectionPct = section.items.length > 0 ? Math.round(sectionDone / section.items.length * 100) : 0;
          return (
            <div key={si} style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>📌 {section.name}</span>
                <div style={{ flex: 1, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: sectionPct === 100 ? '#10b981' : wsColor, width: `${sectionPct}%`, borderRadius: 2, transition: 'width .3s' }} />
                </div>
                <span style={{ fontSize: 10, color: '#9ca3af', minWidth: 30 }}>{sectionDone}/{section.items.length}</span>
              </div>
              {section.items.map(({ item, idx }) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: item.done ? '#f0fdf4' : '#f9fafb', marginBottom: 4, border: '1px solid', borderColor: item.done ? '#bbf7d0' : '#e5e7eb', cursor: 'pointer' }} onClick={() => toggleCheckItem(idx)}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${item.done ? '#10b981' : '#d1d5db'}`, background: item.done ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20,6 9,17 4,12"></polyline></svg>}
                  </div>
                  <span style={{ flex: 1, fontSize: 13, color: item.done ? '#9ca3af' : '#374151', textDecoration: item.done ? 'line-through' : 'none' }}>{item.text}</span>
                  <button onClick={(e) => { e.stopPropagation(); removeCheckItem(idx); }} style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
                </div>
              ))}
            </div>
          );
        })}
        {noSection.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {noSection.map(({ item, idx }) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: item.done ? '#f0fdf4' : '#f9fafb', marginBottom: 4, border: '1px solid', borderColor: item.done ? '#bbf7d0' : '#e5e7eb', cursor: 'pointer' }} onClick={() => toggleCheckItem(idx)}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${item.done ? '#10b981' : '#d1d5db'}`, background: item.done ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {item.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20,6 9,17 4,12"></polyline></svg>}
                </div>
                <span style={{ flex: 1, fontSize: 13, color: item.done ? '#9ca3af' : '#374151', textDecoration: item.done ? 'line-through' : 'none' }}>{item.text}</span>
                <button onClick={(e) => { e.stopPropagation(); removeCheckItem(idx); }} style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCRMSidebar = () => (
    <div style={{ width: 240, ...glass, borderRadius: 0, borderTop: 'none', borderBottom: 'none', borderLeft: 'none', minHeight: 'calc(100vh - 60px)', padding: '24px 16px', position: 'sticky', top: 60, alignSelf: 'flex-start' }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: '#d1d5db', padding: '0 8px', marginBottom: 12 }}>Projetos CRM</div>
      <div onClick={() => setActiveProject('all')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: activeProject === 'all' ? '#111' : 'transparent', marginBottom: 4 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: activeProject === 'all' ? wsColor : '#d1d5db' }}></div>
        <span style={{ fontSize: 14, fontWeight: 600, color: activeProject === 'all' ? '#fff' : '#6b7280' }}>Todos os projetos</span>
      </div>
      {projects.map((p, i) => {
        const active = activeProject === p.id;
        const projectCards = cards.filter(c => c.project_id === p.id);
        const done = projectCards.filter(c => c.column_name === 'done').length;
        const pColor = getProjectColor(i);
        return (
          <div key={p.id} style={{ marginBottom: 4 }}>
            <div onClick={() => setActiveProject(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: active ? '#111' : 'transparent' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: pColor, flexShrink: 0 }}></div>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: active ? '#fff' : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
              <button onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }} style={{ background: 'none', border: 'none', color: active ? 'rgba(255,255,255,0.4)' : '#d1d5db', cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0 }}>×</button>
            </div>
            <div style={{ marginLeft: 28, marginTop: 2, height: 3, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: pColor, width: projectCards.length > 0 ? `${Math.round(done / projectCards.length * 100)}%` : '0%', borderRadius: 2 }} />
            </div>
            <div style={{ marginLeft: 28, fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{done}/{projectCards.length} cards</div>
          </div>
        );
      })}
      <button onClick={() => setShowNewProject(true)} style={{ width: '100%', background: 'rgba(139,92,246,0.1)', border: '1px dashed #8b5cf6', borderRadius: 8, padding: '9px', color: '#8b5cf6', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 14, fontFamily: 'inherit' }}>+ Novo projeto CRM</button>
    </div>
  );

  const renderOnboardingSidebar = () => (
    <div style={{ width: 240, ...glass, borderRadius: 0, borderTop: 'none', borderBottom: 'none', borderLeft: 'none', minHeight: 'calc(100vh - 60px)', padding: '24px 16px', position: 'sticky', top: 60, alignSelf: 'flex-start', overflowY: 'auto' }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: '#d1d5db', padding: '0 8px', marginBottom: 12 }}>Clientes</div>
      {overdueCards > 0 && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 12, fontSize: 11, color: '#dc2626', fontWeight: 700 }}>⚠️ {overdueCards} cliente{overdueCards > 1 ? 's' : ''} em atraso</div>}
      {cards.filter(c => c.column_name !== 'implementado').map(card => {
        const overdue = isOverdue(card.due_date);
        const checkDone = (card.checklist || []).filter((i: any) => i.done).length;
        const checkTotal = (card.checklist || []).length;
        const pct = checkTotal > 0 ? Math.round(checkDone / checkTotal * 100) : 0;
        return (
          <div key={card.id} onClick={(e) => openEditCard(card, e)} style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', background: overdue ? '#fef2f2' : '#f9fafb', border: `1px solid ${overdue ? '#fecaca' : '#e5e7eb'}`, borderLeft: `3px solid ${overdue ? '#ef4444' : wsColor}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.title}</div>
            <div style={{ fontSize: 10, color: overdue ? '#ef4444' : '#9ca3af', marginBottom: 6 }}>{ONBOARDING_ICONS[card.column_name]} {ONBOARDING_LABELS[card.column_name]}{overdue && ' · ⚠️ Atrasado'}</div>
            <div style={{ height: 3, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: overdue ? '#ef4444' : wsColor, width: `${pct}%`, borderRadius: 2 }} />
            </div>
            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 3 }}>{checkDone}/{checkTotal} itens</div>
          </div>
        );
      })}
      {cards.filter(c => c.column_name !== 'implementado').length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>Nenhum cliente ativo</div>}
    </div>
  );

  const renderDemandasSidebar = () => {
    const manhattanCards = cards.filter(c => clients.find(cl => cl.id === c.client_id)?.team === 'MANHATTAN' && c.column_name !== 'done');
    const brooklynCards = cards.filter(c => clients.find(cl => cl.id === c.client_id)?.team === 'BROOKLYN' && c.column_name !== 'done');
    return (
      <div style={{ width: 240, ...glass, borderRadius: 0, borderTop: 'none', borderBottom: 'none', borderLeft: 'none', minHeight: 'calc(100vh - 60px)', padding: '24px 16px', position: 'sticky', top: 60, alignSelf: 'flex-start', overflowY: 'auto' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: '#d1d5db', padding: '0 8px', marginBottom: 12 }}>Filtros</div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Equipe</div>
          {['all', 'MANHATTAN', 'BROOKLYN'].map(team => (
            <div key={team} onClick={() => setActiveTeamFilter(team)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: activeTeamFilter === team ? (team === 'all' ? '#111' : TEAM_COLORS[team]) : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {team !== 'all' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: activeTeamFilter === team ? '#fff' : TEAM_COLORS[team] }}></div>}
                <span style={{ fontSize: 13, fontWeight: 600, color: activeTeamFilter === team ? '#fff' : '#374151' }}>{team === 'all' ? 'Todas' : team}</span>
              </div>
              <span style={{ fontSize: 11, color: activeTeamFilter === team ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontWeight: 700 }}>
                {team === 'all' ? cards.filter(c => c.column_name !== 'done').length : team === 'MANHATTAN' ? manhattanCards.length : brooklynCards.length}
              </span>
            </div>
          ))}
        </div>
        <div style={{ height: 1, background: '#e5e7eb', marginBottom: 16 }} />
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Membro</div>
          <div onClick={() => setActiveMemberFilter('all')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: activeMemberFilter === 'all' ? '#111' : 'transparent' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: activeMemberFilter === 'all' ? '#fff' : '#374151' }}>Todos</span>
            <span style={{ fontSize: 11, color: activeMemberFilter === 'all' ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontWeight: 700 }}>{cards.filter(c => c.column_name !== 'done').length}</span>
          </div>
          {TEAM_MEMBERS.map(m => {
            const memberCards = cards.filter(c => (c.members || []).includes(m) && c.column_name !== 'done');
            const memberOverdue = memberCards.filter(c => isOverdue(c.due_date)).length;
            const active = activeMemberFilter === m;
            return (
              <div key={m} onClick={() => setActiveMemberFilter(m)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: active ? '#111' : 'transparent' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: active ? wsColor : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: active ? '#fff' : '#6b7280', flexShrink: 0 }}>{m[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: active ? '#fff' : '#374151' }}>{m}</div>
                  <div style={{ fontSize: 10, color: active ? 'rgba(255,255,255,0.6)' : '#9ca3af' }}>{memberCards.length} demandas{memberOverdue > 0 ? ` · ⚠️ ${memberOverdue}` : ''}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ height: 1, background: '#e5e7eb', marginBottom: 16 }} />
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Resumo</div>
        {[{ label: 'Total', value: totalCards, color: '#111' }, { label: 'Concluídos', value: doneCards, color: '#16a34a' }, { label: 'Atrasados', value: overdueCards, color: '#dc2626' }].map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderRadius: 8, marginBottom: 4, background: '#f9fafb' }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: s.color }}>{s.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8f8f6 0%, #f0ede8 30%, #f5f3f0 60%, #eef0f5 100%)', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#111827' }}>
      <div style={{ ...glass, borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/Logo-Final-Preto (1).png" alt="Empire Ads" onClick={onBack} style={{ height: 36, objectFit: 'contain', cursor: 'pointer' }} title="Voltar para início" />
          <div style={{ width: 1, height: 24, background: '#e5e7eb' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>{isCRM ? '🗂️' : isOnboarding ? '🚀' : '⚡'}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{board?.name}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {!isOnboarding && <button onClick={() => setShowTagManager(true)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>🏷️ Tags</button>}
          <span style={{ fontSize: 13, color: '#9ca3af' }}>{session.user.email}</span>
          <button onClick={async () => { await supabase.auth.signOut(); }} style={{ ...btn(false), padding: '6px 14px', fontSize: 13 }}>Sair</button>
        </div>
      </div>

      <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
        {isCRM && renderCRMSidebar()}
        {isOnboarding && renderOnboardingSidebar()}
        {isDemandas && renderDemandasSidebar()}

        <div style={{ flex: 1, padding: '24px 24px 60px', overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 16 }}>
            {[{ label: 'Total de cards', value: totalCards, color: '#111' }, { label: 'Concluídos', value: doneCards, color: '#16a34a' }, { label: 'Atrasados', value: overdueCards, color: '#dc2626' }].map(s => (
              <div key={s.label} style={{ ...glass, borderRadius: 12, padding: '18px 22px', borderLeft: `3px solid ${s.color}` }}>
                <div style={{ fontSize: 34, fontWeight: 800, color: s.color, letterSpacing: -1 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {availableSprints.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, textTransform: 'uppercase' }}>Sprint:</span>
              <button onClick={() => setActiveSprint(null)} style={{ padding: '5px 14px', borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: activeSprint === null ? '#111' : '#f3f4f6', color: activeSprint === null ? '#fff' : '#6b7280', fontFamily: 'inherit' }}>Todos</button>
              {availableSprints.map((s: any) => (
                <button key={s} onClick={() => setActiveSprint(activeSprint === s ? null : s)} style={{ padding: '5px 14px', borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: activeSprint === s ? wsColor : '#f3f4f6', color: activeSprint === s ? '#fff' : '#6b7280', fontFamily: 'inherit' }}>Sprint {s}</button>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 60, fontSize: 15 }}>Carregando...</div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMNS.length},minmax(260px,1fr))`, gap: 14 }}>
                {COLUMNS.map(col => (
                  <div key={col} style={{ ...glass, borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `3px solid ${wsColor}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>{COLUMN_ICONS[col]}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', color: '#111' }}>{COLUMN_LABELS[col]}</span>
                      </div>
                      <div style={{ fontSize: 12, fontFamily: 'monospace', background: '#f3f4f6', padding: '3px 9px', borderRadius: 100, color: '#9ca3af', border: '1px solid #e5e7eb', fontWeight: 700 }}>{filteredCards(col).length}</div>
                    </div>
                    <Droppable droppableId={col}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} style={{ padding: '10px', minHeight: 100, background: snapshot.isDraggingOver ? `${wsColor}08` : 'transparent', transition: 'background .2s' }}>
                          {filteredCards(col).map((card, index) => {
                            const overdue = isOverdue(card.due_date) && col !== 'done';
                            const todayDue = isToday(card.due_date) && col !== 'done';
                            const [tbg, tcol] = TYPE_COLORS[card.card_type] || TYPE_COLORS.task;
                            const cardTags = tags.filter(t => (card.tags || []).includes(t.id));
                            const isHovered = hoveredCard === card.id;
                            const checkDone = (card.checklist || []).filter((i: any) => i.done).length;
                            const checkTotal = (card.checklist || []).length;
                            const projIndex = projects.findIndex(p => p.id === card.project_id);
                            const cardColor = isCRM && projIndex >= 0 ? getProjectColor(projIndex) : wsColor;
                            const cardClient = clients.find(cl => cl.id === card.client_id);
                            return (
                              <Draggable key={card.id} draggableId={card.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                    onMouseEnter={() => setHoveredCard(card.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    onClick={(e) => openEditCard(card, e)}
                                    style={{
                                      ...provided.draggableProps.style,
                                      background: snapshot.isDragging ? 'rgba(255,255,255,0.98)' : '#fff',
                                      border: `1px solid ${overdue ? '#fecaca' : todayDue ? '#fde68a' : card.urgency ? '#fde68a' : '#e5e5e0'}`,
                                      borderLeft: `3.5px solid ${card.urgency ? '#f59e0b' : overdue ? '#ef4444' : cardColor}`,
                                      borderRadius: 10, padding: '12px 13px', marginBottom: 8, position: 'relative',
                                      boxShadow: snapshot.isDragging ? '0 12px 32px rgba(0,0,0,0.15)' : isHovered ? '0 6px 20px rgba(0,0,0,0.11)' : '0 1px 4px rgba(0,0,0,0.07)',
                                      transform: snapshot.isDragging ? provided.draggableProps.style?.transform : isHovered ? 'translateY(-2px)' : 'none',
                                      transition: snapshot.isDragging ? 'none' : 'box-shadow 0.15s, transform 0.15s',
                                      cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                    }}
                                  >
                                    {card.priority > 0 && <div style={{ position: 'absolute', top: -8, left: 10, background: '#111', color: '#F5A800', fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 }}>#{card.priority}</div>}
                                    {card.urgency && <div style={{ position: 'absolute', top: -8, right: 10, background: '#f59e0b', color: '#fff', fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 }}>⚡ URGENTE</div>}
                                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 7, marginTop: card.priority > 0 ? 4 : 0 }}>
                                      {cardClient && <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4, background: `${TEAM_COLORS[cardClient.team]}18`, color: TEAM_COLORS[cardClient.team] }}>{cardClient.team}</span>}
                                      {!isOnboarding && !isCRM && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, background: tbg, color: tcol }}>{card.card_type}</span>}
                                      {card.sprint && !isOnboarding && !isCRM && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#8a8a80', marginTop: 1, padding: '2px 4px' }}>S{card.sprint}</span>}
                                      {card.recurrence_type && card.recurrence_type !== 'none' && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#ede9ff', color: '#7c3aed' }}>🔁</span>}
                                      {cardTags.map((t: any) => <span key={t.id} style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: `${t.color}18`, color: t.color }}>{t.name}</span>)}
                                    </div>
                                    {cardClient && <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4, fontWeight: 600 }}>📍 {cardClient.name}</div>}
                                    <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35, marginBottom: 8, color: '#1a1a18' }}>{card.title}</div>
                                    {card.description && renderDescription(card.description)}
                                    {checkTotal > 0 && (
                                      <div style={{ marginTop: 6 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                          <div style={{ flex: 1, height: 4, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', background: checkDone === checkTotal ? '#10b981' : wsColor, width: `${Math.round(checkDone / checkTotal * 100)}%`, borderRadius: 2, transition: 'width .3s' }} />
                                          </div>
                                          <span style={{ fontSize: 9, color: '#8a8a80', fontFamily: 'monospace' }}>{checkDone}/{checkTotal}</span>
                                        </div>
                                      </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                                      <div style={{ display: 'flex', gap: 3 }}>
                                        {(card.members || []).map((m: string, mi: number) => (
                                          <div key={mi} title={m} style={{ width: 22, height: 22, borderRadius: '50%', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>{m[0]}</div>
                                        ))}
                                      </div>
                                      {card.due_date && (
                                        <div style={{ fontSize: 11, color: overdue ? '#ef4444' : todayDue ? '#f59e0b' : '#8a8a80', display: 'flex', alignItems: 'center', gap: 4, fontWeight: todayDue || overdue ? 700 : 400 }}>
                                          {overdue ? '⚠️' : todayDue ? '🔔' : '📅'} {new Date(card.due_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          <button onClick={(e) => { e.stopPropagation(); setShowNewCard(col); }} style={{ width: '100%', background: 'transparent', border: '1px dashed #ddddd4', borderRadius: 10, padding: '10px', color: '#8a8a80', fontSize: 13, cursor: 'pointer', marginTop: 4, fontFamily: 'inherit' }}>+ Card</button>
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          )}
        </div>
      </div>

      {/* MODAL EDITAR CARD */}
      {editingCard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => { setEditingCard(null); fetchAll(); }}>
          <div style={{ ...glassModal, borderRadius: 20, width: '100%', maxWidth: 820, maxHeight: '90vh', overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 260px', gap: 0 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px 28px', borderRight: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  {editForm.urgency && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef3c7', color: '#d97706', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6, marginBottom: 8 }}>⚡ URGENTE</div>}
                  <input style={{ width: '100%', fontSize: 18, fontWeight: 800, color: '#111', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', lineHeight: 1.3 }} value={editForm.demanda || ''} onChange={e => updateField('demanda', e.target.value)} placeholder="TÍTULO DA DEMANDA" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 12, flexShrink: 0 }}>
                  {saving && <span style={{ fontSize: 11, color: '#9ca3af' }}>Salvando...</span>}
                  {savedOk && !saving && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>✓ Salvo</span>}
                  <button onClick={() => { setEditingCard(null); fetchAll(); }} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer' }}>✕</button>
                </div>
              </div>

              {isCRM && (
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Projeto</label>
                  <select style={{ ...input, marginBottom: 0, fontSize: 12 }} value={editForm.project_id} onChange={e => updateField('project_id', e.target.value, true)}>
                    <option value="">Selecionar...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}

              {!isOnboarding && !isCRM && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                  <div>
                    <label style={lbl}>Tipo</label>
                    <select style={{ ...input, marginBottom: 0, fontSize: 12 }} value={editForm.card_type} onChange={e => updateField('card_type', e.target.value, true)}>
                      <option value="task">Task</option>
                      <option value="config">Config</option>
                      <option value="video">Vídeo</option>
                      <option value="entrega">Entrega</option>
                      <option value="reuniao">Reunião</option>
                      <option value="robo">Robô</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Sprint</label>
                    <input style={{ ...input, marginBottom: 0, fontSize: 12 }} type="number" min={1} max={10} value={editForm.sprint} onChange={e => updateField('sprint', parseInt(e.target.value))} />
                  </div>
                </div>
              )}

              {tags.length > 0 && !isOnboarding && (
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Etiquetas</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {tags.map((t: any) => {
                      const selected = (editForm.tags || []).includes(t.id);
                      return <div key={t.id} onClick={() => updateField('tags', selected ? editForm.tags.filter((id: string) => id !== t.id) : [...editForm.tags, t.id], true)} style={{ padding: '4px 10px', borderRadius: 6, background: selected ? t.color : `${t.color}15`, color: selected ? '#fff' : t.color, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1px solid ${t.color}40` }}>{t.name}</div>;
                    })}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Descrição</label>
                <textarea style={{ ...input, height: 80, resize: 'vertical' as any, marginBottom: 0, fontSize: 12 }} placeholder="Descreva a demanda..." value={editForm.description} onChange={e => updateField('description', e.target.value)} />
              </div>

              {!isCRM && (
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Checklist</label>
                  {renderChecklist()}
                  <div style={{ marginTop: 12, background: '#f9fafb', borderRadius: 10, padding: '12px', border: '1px solid #e5e7eb' }}>
                    {isOnboarding && (
                      <select value={newCheckSection} onChange={e => setNewCheckSection(e.target.value)} style={{ width: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', color: '#374151', fontSize: 12, outline: 'none', marginBottom: 8, fontFamily: 'inherit' }}>
                        <option value="">Sem seção</option>
                        {ONBOARDING_CHECKLIST_TEMPLATE.map(s => <option key={s.section} value={s.section}>{s.section}</option>)}
                      </select>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input style={{ ...input, marginBottom: 0, flex: 1, fontSize: 12 }} placeholder="Adicionar item..." value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCheckItem()} />
                      <button onClick={addCheckItem} style={{ ...btn(true), padding: '10px 16px', fontSize: 13 }}>+</button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <button onClick={() => { if (window.confirm('Excluir este card?')) deleteCard(editingCard.id); }} style={{ ...btn(false), color: '#ef4444', background: '#fef2f2', fontSize: 13 }}>🗑️ Excluir card</button>
              </div>
            </div>

            {/* DIREITA */}
            <div style={{ padding: '32px 20px', background: 'rgba(248,248,246,0.6)' }}>

              {isDemandas && (
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Prioridade</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <div onClick={() => updateField('priority', 0, true)} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: editForm.priority === 0 ? '#111' : '#f3f4f6', color: editForm.priority === 0 ? '#fff' : '#6b7280', border: `1px solid ${editForm.priority === 0 ? '#111' : '#e5e7eb'}` }}>
                      Sem
                    </div>
                    {[1, 2, 3, 4, 5].map(n => (
                      <div key={n} onClick={() => updateField('priority', n, true)} style={{ width: 36, height: 36, borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: editForm.priority === n ? '#F5A800' : '#f3f4f6', color: editForm.priority === n ? '#fff' : '#6b7280', border: `1px solid ${editForm.priority === n ? '#F5A800' : '#e5e7eb'}` }}>
                        {n}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6 }}>1 = maior prioridade</div>
                </div>
              )}

              {isDemandas && (
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Cliente</label>
                  <select style={{ ...input, marginBottom: 0, fontSize: 12 }} value={editForm.client_id} onChange={e => updateField('client_id', e.target.value, true)}>
                    <option value="">Sem cliente</option>
                    <optgroup label="MANHATTAN">
                      {clients.filter(c => c.team === 'MANHATTAN').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                    <optgroup label="BROOKLYN">
                      {clients.filter(c => c.team === 'BROOKLYN').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  </select>
                  {editForm.client_id && (
                    <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: TEAM_COLORS[clients.find(c => c.id === editForm.client_id)?.team] || '#111' }}>
                      Equipe: {clients.find(c => c.id === editForm.client_id)?.team}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Urgência</label>
                <div onClick={() => updateField('urgency', !editForm.urgency, true)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: editForm.urgency ? '#fef3c7' : '#f3f4f6', border: `1px solid ${editForm.urgency ? '#fde68a' : '#e5e7eb'}`, cursor: 'pointer' }}>
                  <div style={{ width: 36, height: 20, borderRadius: 100, background: editForm.urgency ? '#f59e0b' : '#d1d5db', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 2, left: editForm.urgency ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: editForm.urgency ? '#d97706' : '#6b7280' }}>{editForm.urgency ? '⚡ Urgente' : 'Normal'}</span>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Data de entrega</label>
                <input style={{ ...input, marginBottom: 0, fontSize: 12 }} type="date" value={editForm.due_date} onChange={e => updateField('due_date', e.target.value, true)} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Membros</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {TEAM_MEMBERS.map(m => {
                    const selected = (editForm.members || []).includes(m);
                    return (
                      <div key={m} onClick={() => updateField('members', selected ? editForm.members.filter((x: string) => x !== m) : [...(editForm.members || []), m], true)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: selected ? '#111' : '#f3f4f6', cursor: 'pointer', border: `1px solid ${selected ? '#111' : '#e5e7eb'}` }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: selected ? wsColor : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{m[0]}</div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: selected ? '#fff' : '#374151' }}>{m}</span>
                        {selected && <span style={{ marginLeft: 'auto', fontSize: 12, color: wsColor }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {!isOnboarding && (
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>🔁 Recorrência</label>
                  <select style={{ ...input, marginBottom: 10, fontSize: 12 }} value={editForm.recurrence_type} onChange={e => updateField('recurrence_type', e.target.value, true)}>
                    <option value="none">Sem recorrência</option>
                    <option value="daily">Diária</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensal</option>
                  </select>
                  {editForm.recurrence_type === 'weekly' && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {WEEKDAYS.map(d => {
                        const sel = (editForm.recurrence_days || []).includes(d);
                        return <div key={d} onClick={() => updateField('recurrence_days', sel ? editForm.recurrence_days.filter((x: string) => x !== d) : [...(editForm.recurrence_days || []), d], true)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: sel ? '#111' : '#f3f4f6', color: sel ? '#fff' : '#6b7280', border: `1px solid ${sel ? '#111' : '#e5e7eb'}` }}>{d}</div>;
                      })}
                    </div>
                  )}
                  {editForm.recurrence_type === 'monthly' && (
                    <input style={{ ...input, marginBottom: 10, fontSize: 12 }} type="number" min={1} max={31} placeholder="Dia do mês" value={editForm.recurrence_day_of_month || ''} onChange={e => updateField('recurrence_day_of_month', parseInt(e.target.value), true)} />
                  )}
                  {editForm.recurrence_type !== 'none' && (
                    <input style={{ ...input, marginBottom: 0, fontSize: 12 }} type="time" value={editForm.recurrence_time || '09:00'} onChange={e => updateField('recurrence_time', e.target.value, true)} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOVO CARD */}
      {showNewCard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowNewCard(null)}>
          <div style={{ ...glass, borderRadius: 16, padding: '32px', width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111', marginBottom: 24 }}>Novo Card — {COLUMN_ICONS[showNewCard]} {COLUMN_LABELS[showNewCard]}</div>
            {isOnboarding ? (
              <>
                <label style={lbl}>Nome do cliente</label>
                <input style={input} placeholder="Ex: Manhattan Burger" value={newCard.title} onChange={e => setNewCard(n => ({ ...n, title: e.target.value }))} />
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#0369a1' }}>
                  ✅ O checklist completo de onboarding será adicionado automaticamente.
                </div>
              </>
            ) : isCRM ? (
              <>
                <label style={lbl}>Projeto</label>
                <select style={input} value={newCard.project_id} onChange={e => setNewCard(n => ({ ...n, project_id: e.target.value }))}>
                  <option value="">Selecionar projeto...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <label style={lbl}>Demanda</label>
                <input style={input} placeholder="Ex: CONFIGURAR PIPELINE" value={newCard.title} onChange={e => setNewCard(n => ({ ...n, title: e.target.value.toUpperCase() }))} />
              </>
            ) : (
              <>
                <label style={lbl}>Cliente</label>
                <select style={input} value={newCard.client_id} onChange={e => setNewCard(n => ({ ...n, client_id: e.target.value }))}>
                  <option value="">Sem cliente</option>
                  <optgroup label="MANHATTAN">
                    {clients.filter(c => c.team === 'MANHATTAN').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="BROOKLYN">
                    {clients.filter(c => c.team === 'BROOKLYN').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                </select>
                <label style={lbl}>Demanda</label>
                <input style={input} placeholder="Ex: CRIAR CAMPANHA META ADS" value={newCard.title} onChange={e => setNewCard(n => ({ ...n, title: e.target.value.toUpperCase() }))} />
                <label style={lbl}>Checklist</label>
                <select style={input} value={newCard.checklist_template} onChange={e => setNewCard(n => ({ ...n, checklist_template: e.target.value }))}>
                  <option value="">Sem checklist</option>
                  {Object.keys(DEMANDAS_CHECKLIST_TEMPLATES).map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
                {newCard.checklist_template && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#16a34a' }}>
                    ✅ {DEMANDAS_CHECKLIST_TEMPLATES[newCard.checklist_template].length} itens serão adicionados automaticamente.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={lbl}>Tipo</label>
                    <select style={input} value={newCard.card_type} onChange={e => setNewCard(n => ({ ...n, card_type: e.target.value }))}>
                      <option value="task">Task</option>
                      <option value="config">Config</option>
                      <option value="video">Vídeo</option>
                      <option value="entrega">Entrega</option>
                      <option value="reuniao">Reunião</option>
                      <option value="robo">Robô</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Sprint</label>
                    <input style={input} type="number" min={1} max={10} value={newCard.sprint} onChange={e => setNewCard(n => ({ ...n, sprint: parseInt(e.target.value) }))} />
                  </div>
                </div>
              </>
            )}
            <label style={lbl}>Data limite</label>
            <input style={input} type="date" value={newCard.due_date} onChange={e => setNewCard(n => ({ ...n, due_date: e.target.value }))} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewCard(null)} style={btn(false)}>Cancelar</button>
              <button onClick={() => createCard(showNewCard)} style={btn(true)}>Criar Card</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOVO PROJETO CRM */}
      {showNewProject && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowNewProject(false)}>
          <div style={{ ...glass, borderRadius: 16, padding: '32px', width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111', marginBottom: 8 }}>Novo Projeto CRM</div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 24 }}>Os {CRM_CARDS_TEMPLATE.length} cards padrão serão criados automaticamente no Backlog.</div>
            <label style={lbl}>Nome do cliente</label>
            <input style={input} placeholder="Ex: Manhattan Burger" value={newProject.name} onChange={e => setNewProject(n => ({ ...n, name: e.target.value }))} />
            <label style={lbl}>Responsável</label>
            <input style={input} placeholder="Ex: Isa" value={newProject.client} onChange={e => setNewProject(n => ({ ...n, client: e.target.value }))} />
            <label style={lbl}>Entrega V1</label>
            <input style={input} type="date" value={newProject.v1_deadline} onChange={e => setNewProject(n => ({ ...n, v1_deadline: e.target.value }))} />
            <label style={lbl}>Entrega Final</label>
            <input style={input} type="date" value={newProject.deadline} onChange={e => setNewProject(n => ({ ...n, deadline: e.target.value }))} />
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#7c3aed' }}>
              🗂️ Serão criados {CRM_CARDS_TEMPLATE.length} cards automaticamente no Backlog.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewProject(false)} style={btn(false)}>Cancelar</button>
              <button onClick={createProject} disabled={creatingProject} style={{ ...btn(true), opacity: creatingProject ? 0.7 : 1 }}>
                {creatingProject ? 'Criando...' : 'Criar Projeto + Cards'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TAGS */}
      {showTagManager && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowTagManager(false)}>
          <div style={{ ...glass, borderRadius: 16, padding: '32px', width: '100%', maxWidth: 480, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111', marginBottom: 24 }}>🏷️ Gerenciar Tags</div>
            <input style={input} placeholder="Nome da tag..." value={newTag.name} onChange={e => setNewTag(n => ({ ...n, name: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              {TAG_COLORS.map(c => <div key={c} onClick={() => setNewTag(n => ({ ...n, color: c }))} style={{ width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer', border: newTag.color === c ? '3px solid #111' : '2px solid transparent' }} />)}
            </div>
            <button onClick={createTag} style={btn(true)}>Criar Tag</button>
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {tags.map((t: any) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, background: `${t.color}15`, border: `1px solid ${t.color}30` }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: t.color }}>{t.name}</span>
                  <button onClick={() => deleteTag(t.id)} style={{ background: 'none', border: 'none', color: t.color, cursor: 'pointer', fontSize: 12, padding: 0, opacity: 0.6 }}>✕</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setShowTagManager(false)} style={btn(true)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}